import { Checkbox, Collapse, IconButton, useTheme } from "@material-ui/core";
import { Palette } from "@material-ui/core/styles/createPalette";
import React, { useRef, useState } from "react";
import { blueContrast } from "../../hooks/use-map-theme-styles";
import { IDrawerData } from "../../store/lyfe-data-table";
import FlexTypographyComponent from "../FlexTypographyComponent";
import NavigationDown from "../icons/NavigationDown";
import NavigationUp from "../icons/NavigationUp";
import { LiteBox } from "../lite/LiteBox";
import LyfeTooltip from "../LyfeTooltip";
import MappedComponent from "../mapped-components/MappedComponent";

type onRowClick = (event?: React.MouseEvent<Element, MouseEvent> | undefined, rowData?: IDrawerData & object) => void;
type Column = {
	field: string;
	widthAdjustment: number;
	render?: (
		data: any,
		type: string,
		onCellClickAction?: (event: React.MouseEvent<Element, MouseEvent>, value: string | undefined) => void,
		isHovering?: boolean
	) => JSX.Element;
};

interface IRowProps {
	real?: boolean;
	onClick?: onRowClick;
	onCellClickAction?: (event: React.MouseEvent<Element, MouseEvent>, value: string | undefined) => void;
	renderSubRow?: (data: any) => JSX.Element;
	renderAddon?: (data: any) => JSX.Element;
	rowTooltipTitle?: (data: any) => string;
	data: any;
	index: number;
	isActive: boolean;
	columns: Array<Column>;
	onRowCheckBoxClick?: (data: any, index: number, checked: boolean) => void;
	checkBox?: boolean;
	isCheckBoxChecked?: boolean;
	showActionButton?: boolean;
	showActionColumn?: boolean;
	actionButtonCallback?: any;
	disabled?: boolean;
	grayedOut?: boolean;
	isVisible: boolean;
	enableHovering?: boolean;
	isActionGrayedOut?: boolean;
	actionIcon?: React.ReactElement;
	actionTooltip?: string;
	isActionEnabledByRow?: (data: unknown) => boolean;
}

const oddRowStyle = (index: number, palette: any) =>
	index % 2 == 1 ? { backgroundColor: palette.inputTableDivider.main } : { backgroundColor: palette.background.paper };

const getRowStyle = (index: number, isActive: boolean, palette: Palette, disabled?: boolean, grayedOut?: boolean) => {
	const grayedOutValue = grayedOut ? 0.35 : 1;
	const opacity = disabled ? 0.4 : grayedOutValue;
	return {
		cursor: "pointer",
		height: "50px",
		overflow: "hidden",
		outline: isActive ? `1px solid ${palette.primary.main}` : "",
		opacity: opacity,
		...oddRowStyle(index, palette),
	};
};

export default React.memo(function LyfeDataRow(props: IRowProps) {
	const { index, isActive, data, columns, disabled, grayedOut, enableHovering, renderSubRow, rowTooltipTitle } = props;
	const [open, setOpen] = useState(false);
	const [isHovering, setIsHovering] = useState(false);
	const ref = useRef<HTMLTableRowElement>(null);
	const isActionApplicable = props.showActionColumn && (!props.isActionEnabledByRow || props.isActionEnabledByRow(data));

	const onClick = (event?: React.MouseEvent<Element, MouseEvent>) => {
		if (disabled) return;
		props.onClick && props.onClick(event, data);
	};

	function handleCheckBoxChange(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
		props.onRowCheckBoxClick && props.onRowCheckBoxClick(props.data, index, checked);
	}

	const { palette } = useTheme();

	const handleMouseOver = () => {
		enableHovering && setIsHovering(true);
	};

	const handleMouseLeave = () => {
		enableHovering && setIsHovering(false);
	};

	const renderMainRowContent = () => (
		<tr
			ref={ref}
			style={getRowStyle(index, isActive, palette, disabled, grayedOut)}
			onMouseOver={handleMouseOver}
			onMouseLeave={handleMouseLeave}
		>
			{props.checkBox && (
				<td style={{ paddingBottom: 0, paddingLeft: "18px" }}>
					<LiteBox
						height={open ? "70px" : "100%"}
						display="flex"
						alignItems="center"
						borderLeft={
							open ? `5px solid transparent` : `5px solid ${index % 2 === 0 ? palette.background.paper : palette.grey[100]}`
						}
					>
						<Checkbox id={`checkbox${index}`} checked={props.isCheckBoxChecked} onChange={handleCheckBoxChange} />
					</LiteBox>
				</td>
			)}
			{props.renderAddon && <td style={{ width: "50px", paddingLeft: props.checkBox ? 0 : "18px" }}>{props.renderAddon(data)}</td>}
			{renderSubRow && (
				<td style={{ width: "20px", paddingLeft: props.checkBox ? 0 : "18px" }}>
					<IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
						{open ? <NavigationUp /> : <NavigationDown />}
					</IconButton>
				</td>
			)}
			{columns.map((c: any, idx: number) => (
				<td
					key={idx}
					style={{
						width: "100%",
						paddingLeft: idx === 0 ? "32px" : "20px",
						paddingRight: idx === columns.length - 1 && !props.showActionColumn ? "32px" : "20px",
						verticalAlign: "middle",
					}}
					onClick={onClick}
				>
					{!c.render && (
						<FlexTypographyComponent
							containerStyles={c.align && c.align === "right" ? { justifyContent: "flex-end" } : undefined}
							text={data[c.field]}
						/>
					)}
					{c.render && c.render(data, "row", props.onCellClickAction, isHovering)}
				</td>
			))}
			{props.showActionColumn && (
				<td style={{ paddingBottom: 0, paddingLeft: "18px" }}>
					{isActionApplicable && (
						<LiteBox
							height={open ? "70px" : "100%"}
							display="flex"
							alignItems="center"
							borderLeft={
								open
									? `5px solid transparent`
									: `5px solid ${index % 2 === 0 ? palette.background.paper : palette.grey[100]}`
							}
						>
							{props.actionTooltip ? (
								<LyfeTooltip placement="left" maxWidth="500px" title={props.actionTooltip}>
									<IconButton
										size="small"
										color="primary"
										onClick={() => props.actionButtonCallback(data)}
										style={{
											backgroundColor: props.isActionGrayedOut ? palette.grey[300] : "",
										}}
									>
										{props.actionIcon}
									</IconButton>
								</LyfeTooltip>
							) : (
								<IconButton
									size="small"
									onClick={() => props.actionButtonCallback(data)}
									style={{
										backgroundColor: props.isActionGrayedOut ? palette.grey[300] : "",
									}}
								>
									{props.actionIcon}
								</IconButton>
							)}
						</LiteBox>
					)}
				</td>
			)}
		</tr>
	);

	const renderMainRow = () =>
		rowTooltipTitle ? (
			<MappedComponent component="Tooltip" props={{ title: rowTooltipTitle(data) }}>
				{renderMainRowContent()}
			</MappedComponent>
		) : (
			renderMainRowContent()
		);

	return props.isVisible ? (
		<>
			{renderMainRow()}
			{renderSubRow && (
				<tr>
					<td style={{ paddingBottom: 0, paddingTop: 0, backgroundColor: blueContrast }} colSpan={columns.length + 2}>
						<Collapse in={open} timeout="auto" unmountOnExit>
							{renderSubRow && renderSubRow(data)}
						</Collapse>
					</td>
				</tr>
			)}
		</>
	) : (
		<tr ref={ref} onClick={onClick} style={getRowStyle(index, isActive, palette, disabled)}></tr>
	);
});
